import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Rights, User, useUsersQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import ProfileAvatar from '../profiles/ProfileAvatar';
import UserListOption from './UserListOption';
import UserListButtonOption from './UserListButtonOption';
import { getUserProfileFullName } from '../../utils/users';
import { Values } from '../merchants/AddMerchantCashAdvance';

export interface Props {
  id: string;
  disabled: boolean;
  formik: ReturnType<typeof useFormik<Values>>;
}

export const LegalUserAutocomplete = ({ id, disabled, formik }: Props) => {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.legal?.id);
  const inputReference = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<readonly User[]>([]);
  const [userInputValue, setUserInputValue] = useState<string>('');
  const {
    loading: usersLoading,
    data: usersData,
    refetch: usersRefetch
  } = useUsersQuery({
    variables: { right: Rights.IsLegal, isActive: true },
    fetchPolicy: 'network-only'
  });
  const usersFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          usersRefetch({ search: request.input }).then((rq) => setUsers((rq.data.users.data as User[]) || [])),
        200
      ),
    [usersRefetch]
  );
  useEffect(() => setUsers((usersData?.users.data as User[]) || []), [usersData?.users.data, usersLoading]);
  useEffect(() => {
    usersFetch({ input: userInputValue });
  }, [userInputValue, usersFetch]);

  return (
    <>
      {values?.legal?.id && showInfo && (
        <Stack direction={'row'}>
          <UserListButtonOption
            user={values.legal as User}
            type="Legal Name"
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.legal?.id);
          }}
        >
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Partial<User>) => getUserProfileFullName(option as User)}
            options={users}
            autoComplete
            value={(values.legal?.id ? values.legal as User : null)}
            isOptionEqualToValue={(option: User, value: User) =>
              !!value?.id && option.id === value.id
            }
            onChange={(event: SyntheticEvent, newValue: User | null) => {
              setFieldValue('legal', newValue || null);
              setShowInfo(!!newValue?.id);
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setUserInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={'Select Legal'}
                inputRef={inputReference}
                name="legal"
                onBlur={handleBlur}
                error={Boolean(touched.legal?.id && errors.legal?.id)}
                helperText={touched.legal?.id && errors.legal?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.legal?.id && <ProfileAvatar user={values?.legal as User} size={'sm'} />
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return <UserListOption key={key} user={option as User} {...rest} />;
            }}
          />
        </ClickAwayListener>
      )}
    </>
  );
};
